import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CostBreakdownItem } from '../../api/cost';

interface CostChartProps {
  breakdown: CostBreakdownItem[];
  currency?: string;
  totalMonthly: number;
}

type ChartType = 'pie' | 'bar';
type GroupBy = 'provider' | 'type' | 'region';

interface ChartData {
  label: string;
  value: number;
  color: string;
  percentage: number;
}

export const CostChart: React.FC<CostChartProps> = ({
  breakdown,
  currency = 'USD',
  totalMonthly,
}) => {
  const { t } = useTranslation('finops');
  const [chartType, setChartType] = useState<ChartType>('pie');
  const [groupBy, setGroupBy] = useState<GroupBy>('provider');
  const [hoveredSlice, setHoveredSlice] = useState<string | null>(null);
  const [tooltipData, setTooltipData] = useState<{label: string; value: number; percentage: number; x: number; y: number} | null>(null);

  const providerColors: { [key: string]: string } = {
    azure: '#0078D4',
    aws: '#FF9900',
    gcp: '#4285F4',
  };

  const typeColors = [
    '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
    '#06b6d4', '#6366f1', '#14b8a6', '#f97316', '#84cc16',
  ];

  const regionColors = [
    '#1e40af', '#7c3aed', '#be185d', '#b45309', '#047857',
    '#0e7490', '#4338ca', '#0f766e', '#c2410c', '#4d7c0f',
  ];

  // Grouper les données
  const getChartData = (): ChartData[] => {
    const grouped: { [key: string]: number } = {};

    breakdown.forEach((item) => {
      let key: string;
      switch (groupBy) {
        case 'provider':
          key = item.provider || 'unknown';
          break;
        case 'type':
          key = item.resourceType?.split('_').slice(0, 2).join('_') || 'unknown';
          break;
        case 'region':
          key = item.region || 'unknown';
          break;
        default:
          key = 'unknown';
      }

      grouped[key] = (grouped[key] || 0) + (item.monthly || 0);
    });

    // Trier par valeur décroissante et prendre top 10
    const sorted = Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Assigner des couleurs
    const colors = groupBy === 'provider' ? providerColors :
                   groupBy === 'type' ? typeColors : regionColors;

    return sorted.map(([label, value], index) => ({
      label,
      value,
      color: groupBy === 'provider'
        ? (colors as { [key: string]: string })[label.toLowerCase()] || '#666666'
        : (Array.isArray(colors) ? colors[index % colors.length] : '#666666'),
      percentage: (value / totalMonthly) * 100,
    }));
  };

  const chartData = getChartData();

  const formatCost = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatLabel = (label: string): string => {
    if (groupBy === 'provider') {
      const names: { [key: string]: string } = {
        azure: 'Azure',
        aws: 'AWS',
        gcp: 'GCP',
      };
      return names[label.toLowerCase()] || label;
    }
    return label;
  };

  // Rendu du Pie Chart
  const renderPieChart = () => {
    const size = 320;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = size / 2 - 40;

    let currentAngle = -90; // Commencer en haut

    const slices = chartData.map((data) => {
      const sliceAngle = (data.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sliceAngle;

      let pathData: string;

      // Cas spécial pour un cercle complet (100%)
      if (sliceAngle >= 359.99) {
        // Dessiner un cercle complet en deux demi-cercles
        const topX = centerX;
        const topY = centerY - radius;
        const bottomX = centerX;
        const bottomY = centerY + radius;

        pathData = [
          `M ${centerX} ${centerY}`,
          `L ${topX} ${topY}`,
          `A ${radius} ${radius} 0 0 1 ${bottomX} ${bottomY}`,
          `A ${radius} ${radius} 0 0 1 ${topX} ${topY}`,
          'Z',
        ].join(' ');
      } else {
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;

        const x1 = centerX + radius * Math.cos(startRad);
        const y1 = centerY + radius * Math.sin(startRad);
        const x2 = centerX + radius * Math.cos(endRad);
        const y2 = centerY + radius * Math.sin(endRad);

        const largeArc = sliceAngle > 180 ? 1 : 0;

        pathData = [
          `M ${centerX} ${centerY}`,
          `L ${x1} ${y1}`,
          `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
          'Z',
        ].join(' ');
      }

      currentAngle = endAngle;

      return (
        <g key={data.label}>
          <path
            d={pathData}
            fill={data.color}
            stroke="#ffffff"
            strokeWidth="2"
            style={{
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              opacity: hoveredSlice === null || hoveredSlice === data.label ? 1 : 0.4,
              transform: hoveredSlice === data.label ? 'scale(1.05)' : 'scale(1)',
              transformOrigin: `${centerX}px ${centerY}px`,
              filter: hoveredSlice === data.label ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' : 'none'
            }}
            onMouseEnter={(e) => {
              setHoveredSlice(data.label);
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltipData({
                label: formatLabel(data.label),
                value: data.value,
                percentage: data.percentage,
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
            onMouseLeave={() => {
              setHoveredSlice(null);
              setTooltipData(null);
            }}
          />
        </g>
      );
    });

    return (
      <div style={styles.chartContainer}>
        <svg width={size} height={size} style={{ display: 'block', overflow: 'visible' }}>
          {slices}
          {/* Centre blanc */}
          <circle cx={centerX} cy={centerY} r={radius * 0.5} fill="#ffffff" />
          <text
            x={centerX}
            y={centerY - 10}
            textAnchor="middle"
            fontSize="24"
            fontWeight="700"
            fill="#111827"
          >
            {formatCost(totalMonthly)}
          </text>
          <text
            x={centerX}
            y={centerY + 15}
            textAnchor="middle"
            fontSize="14"
            fill="#6b7280"
          >
            {t('costBreakdown.perMonth')}
          </text>
        </svg>
      </div>
    );
  };

  // Rendu du Bar Chart
  const renderBarChart = () => {
    const maxValue = Math.max(...chartData.map((d) => d.value));

    return (
      <div style={styles.barChartContainer}>
        {chartData.map((data) => {
          const barWidth = (data.value / maxValue) * 100;

          return (
            <div key={data.label} style={styles.barRow}>
              <div style={styles.barLabel}>{formatLabel(data.label)}</div>
              <div style={styles.barWrapper}>
                <div
                  style={{
                    ...styles.bar,
                    width: `${barWidth}%`,
                    backgroundColor: data.color,
                  }}
                >
                  <span style={styles.barValue}>
                    {formatCost(data.value)} ({data.percentage.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Légende interactive
  const renderLegend = () => (
    <div style={styles.legend}>
      {chartData.map((data) => (
        <div
          key={data.label}
          style={{
            ...styles.legendItem,
            opacity: hoveredSlice === null || hoveredSlice === data.label ? 1 : 0.4,
            transform: hoveredSlice === data.label ? 'translateX(4px)' : 'translateX(0)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={() => setHoveredSlice(data.label)}
          onMouseLeave={() => setHoveredSlice(null)}
        >
          <div
            style={{
              ...styles.legendColor,
              backgroundColor: data.color,
              boxShadow: hoveredSlice === data.label ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
              transform: hoveredSlice === data.label ? 'scale(1.2)' : 'scale(1)',
              transition: 'all 0.3s ease'
            }}
          />
          <div style={styles.legendLabel}>
            <span style={{
              ...styles.legendName,
              fontWeight: hoveredSlice === data.label ? 600 : 500
            }}>
              {formatLabel(data.label)}
            </span>
            <span style={{
              ...styles.legendValue,
              color: hoveredSlice === data.label ? '#111827' : '#6b7280',
              fontWeight: hoveredSlice === data.label ? 600 : 400
            }}>
              {formatCost(data.value)} ({data.percentage.toFixed(1)}%)
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  // Tooltip
  const renderTooltip = () => {
    if (!tooltipData) return null;
    return (
      <div
        style={{
          position: 'fixed',
          left: tooltipData.x,
          top: tooltipData.y - 80,
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          color: '#fff',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          pointerEvents: 'none',
          zIndex: 1000,
          minWidth: '180px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: '#fff' }}>
          {tooltipData.label}
        </div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#10b981', marginBottom: '4px' }}>
          {formatCost(tooltipData.value)}
        </div>
        <div style={{ fontSize: '12px', color: '#d1d5db' }}>
          {tooltipData.percentage.toFixed(1)}% {t('costBreakdown.ofTotal')}
        </div>
        <div style={{
          position: 'absolute',
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '6px solid rgba(17, 24, 39, 0.95)'
        }} />
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Contrôles améliorés */}
      <div style={styles.controls}>
        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>{t('costChart.chartType')}:</label>
          <div style={styles.buttonGroup}>
            <button
              style={{
                ...styles.button,
                ...(chartType === 'pie' ? styles.buttonActive : {}),
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                setChartType('pie');
                setHoveredSlice(null);
              }}
            >
              {t('costChart.pieChart')}
            </button>
            <button
              style={{
                ...styles.button,
                ...(chartType === 'bar' ? styles.buttonActive : {}),
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                setChartType('bar');
                setHoveredSlice(null);
              }}
            >
              {t('costChart.barChart')}
            </button>
          </div>
        </div>

        <div style={styles.controlGroup}>
          <label style={styles.controlLabel}>{t('costChart.groupBy')}:</label>
          <div style={styles.buttonGroup}>
            <button
              style={{
                ...styles.button,
                ...(groupBy === 'provider' ? styles.buttonActive : {}),
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                setGroupBy('provider');
                setHoveredSlice(null);
              }}
            >
              {t('costChart.provider')}
            </button>
            <button
              style={{
                ...styles.button,
                ...(groupBy === 'type' ? styles.buttonActive : {}),
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                setGroupBy('type');
                setHoveredSlice(null);
              }}
            >
              {t('costChart.type')}
            </button>
            <button
              style={{
                ...styles.button,
                ...(groupBy === 'region' ? styles.buttonActive : {}),
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                setGroupBy('region');
                setHoveredSlice(null);
              }}
            >
              {t('costChart.region')}
            </button>
          </div>
        </div>
      </div>

      {/* Graphique */}
      <div style={styles.chartArea}>
        {chartType === 'pie' ? renderPieChart() : renderBarChart()}
        {renderLegend()}
      </div>

      {/* Tooltip */}
      {renderTooltip()}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '24px',
  },
  controls: {
    display: 'flex',
    gap: '24px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  controlLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#6b7280',
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
  },
  button: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    transition: 'all 0.2s',
  },
  buttonActive: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    borderColor: '#3b82f6',
  },
  chartArea: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
    overflow: 'visible',
  },
  chartContainer: {
    flex: '0 0 auto',
    overflow: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '320px',
    minHeight: '320px',
  },
  barChartContainer: {
    flex: '1 1 400px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '400px',
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  barLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    minWidth: '120px',
    textAlign: 'right',
  },
  barWrapper: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    height: '32px',
    position: 'relative',
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '8px',
    transition: 'width 0.3s ease',
    minWidth: '60px',
  },
  barValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#ffffff',
    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  legend: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '250px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  legendColor: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    flexShrink: 0,
  },
  legendLabel: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  legendName: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  legendValue: {
    fontSize: '13px',
    color: '#6b7280',
  },
};
